// www/src/components/services/sextforce/liveStreamHismith/TipRangesEditor.tsx
import {
    Add as AddIcon,
    ContentPaste as ContentPasteIcon,
    Delete as DeleteIcon,
    Edit as EditIcon,
    Timer as TimerIcon,
    Warning as WarningIcon,
} from '@mui/icons-material';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    DialogContent,
    Divider,
    FormControl,
    FormControlLabel,
    Grid,
    IconButton,
    InputAdornment,
    InputLabel,
    LinearProgress,
    MenuItem,
    Paper,
    Select,
    Slider,
    Stack,
    Switch,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { useCallback, useContext, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { deviceLabel, HismithDeviceItem, HismithSettingItem } from '../../../../hooks/useHismith';
import useHismithSettings from '../../../../hooks/useHismithSettings';
import useSubscriber from '../../../../hooks/useSubscriber';
import { SettingsContext } from '../../../../store/SettingsContext';
import DialogAdaptive from '../../../common/DialogAdaptive';
import StyledCardTitleBar from '../../../common/StyledCardTitleBar';
import StyledDialogActions from '../../../common/StyledDialogActions';

const ANY_DEVICE = '';

const cleanNumberStyle = {
    '& input[type=number]': { '-moz-appearance': 'textfield' },
    '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
        '-webkit-appearance': 'none',
        margin: 0,
    },
};

const doRangesOverlap = (startA: number, endA: number, startB: number, endB: number): boolean => startA < endB && startB < endA;

const percentMarks = [
    { value: 0, label: '0%' },
    { value: 50, label: '50%' },
    { value: 100, label: '100%' },
];

interface TipRangesEditorProps {
    devices: HismithDeviceItem[];
}

const TipRangesEditor = ({ devices }: TipRangesEditorProps) => {
    const settingsContext = useContext(SettingsContext);
    const { enqueueSnackbar } = useSnackbar();
    const { data: subscriber } = useSubscriber();
    const { data: allSettings, saveSetting, deleteSetting, isUpdatingSettings } = useHismithSettings();

    const [isDialogOpen, setDialogOpen] = useState(false);
    const [editingItem, setEditingItem] = useState<Partial<HismithSettingItem> | null>(null);

    const sorted = useMemo(
        () => (allSettings ? [...allSettings].sort((a, b) => a.startDollarAmount - b.startDollarAmount) : []),
        [allSettings],
    );

    const deviceById = useMemo(() => new Map(devices.map(device => [device.deviceId, device])), [devices]);
    const anyHasVibration = devices.some(device => device.hasVibration);
    const anySupportsClimax = devices.some(device => device.supportsClimax);

    const targetName = useCallback(
        (deviceId: string | null): string => {
            if (deviceId === null) return 'Any device';

            const device = deviceById.get(deviceId);

            return device ? deviceLabel(device) : 'Deleted device';
        },
        [deviceById],
    );

    // Capability of the current edit target: a specific device's, or the union for Any device.
    const targetCaps = useMemo(() => {
        if (!editingItem) return { hasVibration: false, supportsClimax: false };

        if (editingItem.deviceId) {
            const device = deviceById.get(editingItem.deviceId);

            return { hasVibration: device?.hasVibration ?? false, supportsClimax: device?.supportsClimax ?? false };
        }

        return { hasVibration: anyHasVibration, supportsClimax: anySupportsClimax };
    }, [editingItem, deviceById, anyHasVibration, anySupportsClimax]);

    const validationError = useMemo(() => {
        if (!editingItem) return null;

        const start = editingItem.startDollarAmount ?? 0;
        const end = editingItem.endingDollarAmount ?? 0;
        const duration = editingItem.durationPerDollar ?? 0;

        if (start < 1) return 'Start amount must be at least $1.';
        if (end <= start) return 'End amount must be greater than start amount.';
        if (end > 200) return 'Maximum tip amount cannot exceed $200.';
        if (duration <= 0) return 'Duration must be greater than 0.';

        const target = editingItem.deviceId ?? null;
        const others = sorted.filter(s => s.settingId !== editingItem.settingId && (s.deviceId ?? null) === target);

        for (const other of others) {
            if (doRangesOverlap(start, end, other.startDollarAmount, other.endingDollarAmount)) {
                return `Overlaps an existing ${targetName(target)} range ($${other.startDollarAmount}-$${other.endingDollarAmount}).`;
            }
        }

        return null;
    }, [editingItem, sorted, targetName]);

    const handleOpenDialog = (item?: HismithSettingItem) => {
        if (item) {
            setEditingItem({ ...item });
        } else {
            const anyRanges = sorted.filter(s => s.deviceId === null);
            const maxEnd = anyRanges.reduce((max, curr) => Math.max(max, curr.endingDollarAmount), 0);
            const suggestedStart = maxEnd > 0 ? Math.min(maxEnd + 1, 200) : 1;

            setEditingItem({
                onlyfansId: subscriber?.ofUserId ?? 0,
                deviceId: null,
                startDollarAmount: suggestedStart,
                endingDollarAmount: Math.min(suggestedStart + 9, 200),
                durationMode: 'dynamic',
                durationPerDollar: 1,
                speed: 50,
                vibrationSpeed: null,
                climax: false,
            });
        }

        setDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setDialogOpen(false);
        setEditingItem(null);
    };

    const handleTargetChange = (value: string) => {
        if (!editingItem) return;

        const deviceId = value === ANY_DEVICE ? null : value;
        const caps = deviceId
            ? deviceById.get(deviceId) ?? { hasVibration: false, supportsClimax: false }
            : { hasVibration: anyHasVibration, supportsClimax: anySupportsClimax };

        setEditingItem({
            ...editingItem,
            deviceId,
            vibrationSpeed: caps.hasVibration ? editingItem.vibrationSpeed ?? null : null,
            climax: caps.supportsClimax ? editingItem.climax ?? false : false,
        });
    };

    const handleRangeChange = (_: Event, newValue: number | number[], activeThumb: number) => {
        if (!Array.isArray(newValue) || !editingItem) return;

        const [newStart, newEnd] = newValue;

        if (activeThumb === 0) {
            setEditingItem({ ...editingItem, startDollarAmount: Math.min(newStart, (editingItem.endingDollarAmount || 200) - 1) });
        } else {
            setEditingItem({ ...editingItem, endingDollarAmount: Math.max(newEnd, (editingItem.startDollarAmount || 1) + 1) });
        }
    };

    const handleSave = async () => {
        if (!editingItem || validationError) return;

        const payload: HismithSettingItem = {
            settingId: editingItem.settingId,
            onlyfansId: subscriber?.ofUserId ?? 0,
            deviceId: editingItem.deviceId ?? null,
            startDollarAmount: Number(editingItem.startDollarAmount),
            endingDollarAmount: Number(editingItem.endingDollarAmount),
            durationMode: editingItem.durationMode ?? 'dynamic',
            durationPerDollar: Number(editingItem.durationPerDollar),
            speed: Number(editingItem.speed ?? 0),
            vibrationSpeed:
                editingItem.vibrationSpeed === null || editingItem.vibrationSpeed === undefined ? null : Number(editingItem.vibrationSpeed),
            climax: editingItem.climax ?? false,
        };

        try {
            await saveSetting(payload);
            enqueueSnackbar(`Tip range ${payload.settingId ? 'updated' : 'created'}.`, { variant: 'success', autoHideDuration: 3000 });
            handleCloseDialog();
        } catch (error) {
            enqueueSnackbar(`Failed to save tip range: ${error instanceof Error ? error.message : error}`, {
                variant: 'error',
                autoHideDuration: 4000,
            });
        }
    };

    const handleDelete = async (settingId: string | undefined) => {
        if (!settingId) return;

        try {
            const result = await deleteSetting(settingId);

            enqueueSnackbar(result.success ? 'Tip range deleted.' : 'Tip range not found.', {
                variant: result.success ? 'success' : 'warning',
                autoHideDuration: 3000,
            });
        } catch (error) {
            enqueueSnackbar(`Failed to delete tip range: ${error instanceof Error ? error.message : error}`, { variant: 'error' });
        }
    };

    const tipMenuText = (): string =>
        `Tip Menu\n--------\n` +
        sorted
            .map(s => {
                const prefix = s.deviceId ? `${targetName(s.deviceId)}: ` : '';
                const vib = s.vibrationSpeed !== null ? ` 💨 ${s.vibrationSpeed}%` : '';
                const climax = s.climax ? ' 💥' : '';

                return `${prefix}Tip $${s.startDollarAmount}-$${s.endingDollarAmount}: 🏎️ ${s.speed}%${vib}${climax} ⏱️ ${
                    s.durationPerDollar
                } sec${s.durationMode === 'dynamic' ? ' per $' : ''}`;
            })
            .join('\n');

    return (
        <Box>
            <Button
                variant="contained"
                fullWidth
                startIcon={<AddIcon />}
                onClick={() => handleOpenDialog()}
                disabled={isUpdatingSettings}
                sx={{ mb: 2 }}
            >
                Add Range
            </Button>

            {isUpdatingSettings && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}

            <Stack spacing={2}>
                {sorted.length === 0 && (
                    <Paper sx={{ p: 4, textAlign: 'center', bgcolor: 'background.default' }} variant="outlined">
                        <Typography color="text.secondary">No tip ranges configured yet.</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            Ranges with the same target must not overlap. Maximum tip amount is $200.
                        </Typography>
                    </Paper>
                )}

                {sorted.map(setting => (
                    <Card key={setting.settingId} variant="outlined">
                        <CardContent>
                            <Grid container alignItems="center" spacing={2}>
                                <Grid item xs="auto">
                                    <Box
                                        sx={{
                                            bgcolor: 'success.main',
                                            color: 'success.contrastText',
                                            borderRadius: 2,
                                            minWidth: 110,
                                            height: 56,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            px: 1,
                                            boxShadow: 2,
                                        }}
                                    >
                                        <Typography fontWeight="bold" variant="subtitle1" sx={{ lineHeight: 1.2 }}>
                                            ${setting.startDollarAmount} - ${setting.endingDollarAmount}
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            sx={{ opacity: 0.9, fontSize: '0.65rem', textTransform: 'uppercase' }}
                                        >
                                            Range
                                        </Typography>
                                    </Box>
                                </Grid>

                                <Grid item xs>
                                    <Stack spacing={1}>
                                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                            <Chip
                                                size="small"
                                                label={targetName(setting.deviceId)}
                                                color={setting.deviceId ? 'primary' : 'default'}
                                            />
                                            {setting.climax && <Chip size="small" variant="outlined" label="Climax" />}
                                        </Stack>
                                        <Box display="flex" alignItems="center" gap={0.5}>
                                            <TimerIcon fontSize="small" color="action" />
                                            <Typography variant="body2">
                                                <b>{setting.durationPerDollar}s</b>
                                                {setting.durationMode === 'dynamic' ? ' per dollar' : ''}
                                            </Typography>
                                        </Box>
                                        <Divider flexItem />
                                        <Typography variant="caption" color="text.secondary">
                                            Speed {setting.speed}%
                                            {setting.vibrationSpeed !== null ? ` · Vibration ${setting.vibrationSpeed}%` : ''}
                                        </Typography>
                                    </Stack>
                                </Grid>

                                <Grid item xs="auto" display="flex" flexDirection="column" gap={1} alignItems="flex-end">
                                    <IconButton
                                        onClick={() => handleOpenDialog(setting)}
                                        color="primary"
                                        size="small"
                                        disabled={isUpdatingSettings}
                                    >
                                        <EditIcon />
                                    </IconButton>
                                    <IconButton
                                        onClick={() => handleDelete(setting.settingId)}
                                        color="error"
                                        size="small"
                                        disabled={isUpdatingSettings}
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                ))}

                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    Copy your tip menu to share it during live streams, or paste it into the{' '}
                    <Link
                        to={`/subscribers/${subscriber?._id}/${settingsContext.services.sextforce.homeUrl}/liveStream/autoAnnouncements`}
                        style={{ textDecoration: 'none', fontWeight: 'bold' }}
                    >
                        Auto Announcement
                    </Link>{' '}
                    system.
                </Typography>

                <Button
                    variant="outlined"
                    disabled={sorted.length === 0}
                    fullWidth
                    startIcon={<ContentPasteIcon />}
                    onClick={() => {
                        navigator.clipboard.writeText(tipMenuText());
                        enqueueSnackbar('Tip menu copied to clipboard', { variant: 'info', autoHideDuration: 3000 });
                    }}
                >
                    Copy Tips Menu to Clipboard
                </Button>
            </Stack>

            <DialogAdaptive open={isDialogOpen} onClose={handleCloseDialog} fullWidth maxWidth="sm">
                <StyledCardTitleBar title={editingItem?.settingId ? 'Edit Tip Range' : 'New Tip Range'} />

                {editingItem && (
                    <DialogContent sx={{ p: 1.5 }}>
                        {validationError && (
                            <Alert severity="warning" icon={<WarningIcon />} sx={{ mb: 1 }}>
                                {validationError}
                            </Alert>
                        )}

                        <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 600, mb: 0.5 }}>
                            Target
                        </Typography>
                        <FormControl fullWidth sx={{ mb: 2 }}>
                            <InputLabel id="hismith-target-label">Device</InputLabel>
                            <Select
                                labelId="hismith-target-label"
                                label="Device"
                                value={editingItem.deviceId ?? ANY_DEVICE}
                                onChange={e => handleTargetChange(String(e.target.value))}
                                disabled={devices.length === 0}
                            >
                                <MenuItem value={ANY_DEVICE}>Any device</MenuItem>
                                {devices.map(device => (
                                    <MenuItem key={device.deviceId} value={device.deviceId}>
                                        {deviceLabel(device)}
                                    </MenuItem>
                                ))}
                            </Select>
                            {devices.length === 0 && (
                                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                                    Add a device first to target it specifically. "Any device" ranges apply to every device you add later.
                                </Typography>
                            )}
                        </FormControl>

                        <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 600, mb: 0.5 }}>
                            Tip Range ($)
                        </Typography>
                        <Box
                            sx={{
                                bgcolor: 'action.hover',
                                px: 3,
                                py: 1,
                                borderRadius: 1,
                                border: '1px solid',
                                borderColor: 'divider',
                                mb: 2,
                            }}
                        >
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                <Typography variant="body2" color="text.secondary">
                                    Start: <b>${editingItem.startDollarAmount}</b>
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    End: <b>${editingItem.endingDollarAmount}</b>
                                </Typography>
                            </Box>
                            <Slider
                                value={[editingItem.startDollarAmount || 1, editingItem.endingDollarAmount || 200]}
                                onChange={handleRangeChange}
                                valueLabelDisplay="auto"
                                disableSwap
                                min={1}
                                max={200}
                                marks={[
                                    { value: 1, label: '$1' },
                                    { value: 100, label: '$100' },
                                    { value: 200, label: '$200' },
                                ]}
                            />
                        </Box>

                        <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 600, mb: 0.5 }}>
                            Timing
                        </Typography>
                        <Grid container spacing={2} mb={2}>
                            <Grid item xs={12}>
                                <ToggleButtonGroup
                                    value={editingItem.durationMode || 'dynamic'}
                                    color="primary"
                                    exclusive
                                    fullWidth
                                    onChange={(_, value) => value && setEditingItem({ ...editingItem, durationMode: value })}
                                >
                                    <ToggleButton value="dynamic">Dynamic</ToggleButton>
                                    <ToggleButton value="fixed">Fixed</ToggleButton>
                                </ToggleButtonGroup>
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    label={editingItem.durationMode === 'fixed' ? 'Fixed Duration (seconds)' : 'Duration per $'}
                                    type="number"
                                    fullWidth
                                    sx={cleanNumberStyle}
                                    value={editingItem.durationPerDollar}
                                    onChange={e => {
                                        const val = Number(e.target.value);
                                        if (val >= 0) setEditingItem({ ...editingItem, durationPerDollar: val });
                                    }}
                                    helperText={
                                        editingItem.durationMode === 'fixed'
                                            ? 'Runs for this long regardless of the tip amount (max 600 s).'
                                            : `A $${editingItem.startDollarAmount} tip runs for about ${
                                                  (editingItem.startDollarAmount || 0) * (editingItem.durationPerDollar || 0)
                                              } seconds (max 600 s).`
                                    }
                                    InputProps={{
                                        startAdornment: <TimerIcon color="action" sx={{ mr: 1 }} />,
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                sec{editingItem.durationMode === 'fixed' ? '' : ' / $'}
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Grid>
                        </Grid>

                        <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 600, mb: 0.5 }}>
                            Speed
                        </Typography>
                        <Box
                            sx={{
                                bgcolor: 'action.hover',
                                px: 3,
                                py: 1,
                                borderRadius: 1,
                                border: '1px solid',
                                borderColor: 'divider',
                                mb: 2,
                            }}
                        >
                            <Typography variant="subtitle2" sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                <span>Main motor</span>
                                <span>{editingItem.speed ?? 0}%</span>
                            </Typography>
                            <Slider
                                value={editingItem.speed ?? 0}
                                min={0}
                                max={100}
                                step={5}
                                marks={percentMarks}
                                valueLabelDisplay="auto"
                                valueLabelFormat={v => `${v}%`}
                                onChange={(_, value) => setEditingItem({ ...editingItem, speed: value as number })}
                            />
                        </Box>

                        {targetCaps.hasVibration && (
                            <Box
                                sx={{
                                    bgcolor: 'action.hover',
                                    px: 3,
                                    py: 1,
                                    borderRadius: 1,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    mb: 2,
                                }}
                            >
                                <Typography variant="subtitle2" sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                    <span>Vibration</span>
                                    <span>
                                        {editingItem.vibrationSpeed === null || editingItem.vibrationSpeed === undefined
                                            ? 'unchanged'
                                            : `${editingItem.vibrationSpeed}%`}
                                    </span>
                                </Typography>
                                <Slider
                                    value={editingItem.vibrationSpeed ?? 0}
                                    min={0}
                                    max={100}
                                    step={5}
                                    marks={percentMarks}
                                    valueLabelDisplay="auto"
                                    valueLabelFormat={v => `${v}%`}
                                    onChange={(_, value) => setEditingItem({ ...editingItem, vibrationSpeed: value as number })}
                                />
                                <Button size="small" onClick={() => setEditingItem({ ...editingItem, vibrationSpeed: null })}>
                                    Leave vibration unchanged
                                </Button>
                                {!editingItem.deviceId && (
                                    <Typography variant="caption" color="text.secondary" display="block">
                                        Applied only to devices that have a vibration motor.
                                    </Typography>
                                )}
                            </Box>
                        )}

                        {targetCaps.supportsClimax && (
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={editingItem.climax ?? false}
                                        onChange={e => setEditingItem({ ...editingItem, climax: e.target.checked })}
                                    />
                                }
                                label="Trigger one-click climax"
                            />
                        )}
                    </DialogContent>
                )}

                <StyledDialogActions>
                    <Button onClick={handleCloseDialog}>Cancel</Button>
                    <Button onClick={handleSave} variant="contained" disabled={!editingItem || !!validationError || isUpdatingSettings}>
                        {editingItem?.settingId ? 'Update' : 'Save'}
                    </Button>
                </StyledDialogActions>
            </DialogAdaptive>
        </Box>
    );
};

export default TipRangesEditor;
